import { ordinal } from '@/config/membershipTierWizardOptions'
import {
  BILLING_INTERVALS,
  customersOf,
  DEFAULT_FEE_NAMES,
  defaultInterval,
  intervalKey,
  intervalLabel,
  percentValid,
  questionSteps,
  recurringFeeValid,
  selectedIntervals,
  stepValid,
  tierLabel
} from '@/config/membershipTierWizardSteps'
import { amountOffCents, discountedCents, formatCents } from '@/helpers/memberPricing'
import { NOT_CHOSEN } from '@/helpers/wizardRail'

const CENTS_PER_DOLLAR = 100
const FALLBACK_EXAMPLE = { name: 'meal', priceCents: 1200 }
const SAMPLE_TIER_PERCENT = 10

export { ordinal }

const list = items => (items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}` : items[0])

const lowerFirst = text => text.charAt(0).toLowerCase() + text.slice(1)

const upperFirst = text => text.charAt(0).toUpperCase() + text.slice(1)

const Customers = data => upperFirst(customersOf(data))

const tierSubject = data => upperFirst(tierLabel(data))

const storeNames = (data, stores) => stores.filter(s => data.joining.storeIds.includes(String(s.id))).map(s => s.name)

const money = (cents, currency) => formatCents(cents ?? 0, currency)

const signUpName = data => data.fees.signUp.name.trim() || DEFAULT_FEE_NAMES.signUp

const recurringName = data => data.fees.recurring.name.trim() || DEFAULT_FEE_NAMES.recurring

const feeCadence = (fee, currency) => {
  if (fee.intervalType === 'dollar_amount') return `every ${money(fee.every * CENTS_PER_DOLLAR, currency)} spent`
  return fee.every === 1 ? 'every order' : `every ${ordinal(fee.every)} order`
}

const joiningRow = (data, stores) => {
  const { mode, isDefault } = data.joining
  if (mode === 'invite') return 'Only customers you add'
  if (mode !== 'checkout') return NOT_CHOSEN
  const names = storeNames(data, stores)
  if (!names.length) return 'At checkout · no store picked yet'
  return `At checkout in ${list(names)}${isDefault ? ' · starting membership' : ''}`
}

const PAYMENT_ROW = {
  always: 'charged automatically',
  never: 'they check out each time',
  choice: 'customers choose'
}

const orderingRow = data => {
  const [start, ...extras] = selectedIntervals(data)
  if (!start || !defaultInterval(data)) return 'Repeats · no interval picked yet'
  const repeats = `Repeats ${lowerFirst(intervalLabel(start))}`
  const switching = extras.length ? ` · can switch to ${list(extras.map(i => lowerFirst(intervalLabel(i))))}` : ''
  return `${repeats}${switching} · ${PAYMENT_ROW[data.ordering.autocharge] ?? PAYMENT_ROW.always}`
}

const INCLUDE_NAMES = { repeat: 'repeat orders', mealPlan: 'a meal plan', memberPrices: 'member prices' }

const includesRow = data => {
  const picked = Object.keys(INCLUDE_NAMES)
    .filter(key => data.includes[key])
    .map(key => INCLUDE_NAMES[key])
  return picked.length ? upperFirst(list(picked)) : 'Nothing extra'
}

const billingPhrase = value => (BILLING_INTERVALS.find(i => i.value === value)?.name ?? '').toLowerCase()

const mealPlanRow = (data, currency) =>
  stepValid('mealPlan', data)
    ? `${data.mealPlan.itemCount} meals · ${money(data.mealPlan.priceCents, currency)} ${billingPhrase(data.mealPlan.billingInterval)}`
    : 'Not finished yet'

const amountValid = cents => Number.isInteger(cents) && cents > 0

const pricingRow = (data, currency) => {
  const { mode, percent, amountCents } = data.pricing
  if (mode === 'keep') return 'Current member prices kept'
  if (mode === 'percent')
    return percentValid(percent) ? `${Number(percent)}% off every product` : 'Percentage not set yet'
  if (mode === 'amount')
    return amountValid(amountCents) ? `${money(amountCents, currency)} off every product` : 'Amount not set yet'
  return NOT_CHOSEN
}

const pickedPackages = (data, packages) => packages.filter(p => data.packageIds.includes(p.id)).map(p => p.name)

const newPackageNames = data => data.newPackages.map(p => p.name.trim()).filter(Boolean)

const packagesRow = (data, packages) => {
  const fresh = data.newPackages.map(p => (p.name.trim() ? `${p.name.trim()} (new)` : 'a new package'))
  const names = [...pickedPackages(data, packages), ...fresh]
  return names.length ? list(names) : 'Open packages only'
}

const passRow = (data, currency) => {
  const { pass } = data
  if (!pass.linked) return 'Switched off for this membership'
  const rewards = pass.rewards.length === 1 ? '1 reward' : `${pass.rewards.length} rewards`
  return `${pass.name.trim() || 'Pass'} · ${money(pass.priceCents, currency)} ${billingPhrase(pass.billingInterval)} · ${rewards}`
}

const feesRow = (data, currency) => {
  const { signUp, recurring } = data.fees
  const parts = []
  if (signUp.enabled) parts.push(`${signUpName(data)} ${money(signUp.amountCents, currency)}`)
  if (recurring.enabled) {
    const cadence = recurringFeeValid(recurring) ? ` ${feeCadence(recurring, currency)}` : ''
    parts.push(`${recurringName(data)} ${money(recurring.amountCents, currency)}${cadence}`)
  }
  return parts.length ? parts.join(' · ') : 'No fees'
}

const hasFee = data => data.fees.signUp.enabled || data.fees.recurring.enabled

const ROWS = {
  name: { value: data => data.name.trim() || 'Not named yet' },
  joining: { value: (data, { stores }) => joiningRow(data, stores) },
  includes: {
    value: data => includesRow(data),
    answered: data => Object.values(data.includes).some(Boolean)
  },
  ordering: { value: data => orderingRow(data) },
  mealPlan: { value: (data, { currency }) => mealPlanRow(data, currency) },
  pricing: { value: (data, { currency }) => pricingRow(data, currency) },
  packages: {
    value: (data, { packages }) => packagesRow(data, packages),
    answered: data => data.packageIds.length > 0 || data.newPackages.length > 0
  },
  pass: { value: (data, { currency }) => passRow(data, currency) },
  fees: { value: (data, { currency }) => feesRow(data, currency), answered: hasFee }
}

const rows = (data, details) =>
  questionSteps(data, details.context).map(step => {
    const { value, answered } = ROWS[step.key]
    const finished = details.stepOk ? details.stepOk(step.key) : stepValid(step.key, data)
    const onDefault = Boolean(answered) && !answered(data)
    return {
      key: step.key,
      label: step.label,
      value: value(data, details),
      valid: finished,
      isDefault: onDefault,
      set: finished && (!onDefault || step.index < details.currentIndex)
    }
  })

export const startingTierWarning = (data, { stores = [], currentDefault = null } = {}) => {
  if (!data.joining.isDefault || !currentDefault) return null
  if (data.joining.mode === 'invite') {
    return `${tierSubject(data)} is not offered at checkout, so new customers at checkout will start with no membership instead of '${currentDefault.name}'.`
  }
  if (data.joining.mode !== 'checkout') return null
  const left = stores
    .filter(s => currentDefault.storeIds.includes(String(s.id)) && !data.joining.storeIds.includes(String(s.id)))
    .map(s => s.name)
  if (!left.length) return null
  const verb = left.length > 1 ? 'do' : 'does'
  return `${list(left)} ${verb} not offer ${tierLabel(data)}, so new customers there will start with no membership instead of '${currentDefault.name}'.`
}

const joiningSentence = (data, stores, currentDefault) => {
  const name = tierLabel(data)
  const subject = tierSubject(data)
  if (data.joining.mode === 'invite') {
    const invited = `${subject} is not offered at checkout. You move customers onto ${name} yourself.`
    if (!data.joining.isDefault) return invited
    return `${invited} Customers who are on no other membership are placed on ${name}.`
  }
  if (data.joining.mode !== 'checkout') return null
  const listed = `${subject} is listed under 'Order Frequency?' at checkout in ${list(storeNames(data, stores))}.`
  if (!data.joining.isDefault) return listed
  const starting = `New customers start on ${name}${currentDefault ? `, replacing '${currentDefault.name}'` : ''}.`
  return [listed, starting, startingTierWarning(data, { stores, currentDefault })].filter(Boolean).join(' ')
}

const tierNames = (ids, tiers) => tiers.filter(t => ids.includes(t.id)).map(t => t.name)

const upgradesSentence = (data, tiers) => {
  const upgrades = tierNames(data.joining.upgradeTierIds, tiers)
  const [downgrade] = tierNames(data.joining.downgradeTierId ? [data.joining.downgradeTierId] : [], tiers)
  const customers = Customers(data)
  if (upgrades.length && downgrade)
    return `${customers} can upgrade to ${list(upgrades)}, and drop back to ${downgrade}.`
  if (upgrades.length) return `${customers} can upgrade to ${list(upgrades)}.`
  if (downgrade) return `${customers} drop back to ${downgrade} when they downgrade.`
  return null
}

const PAYMENT_SENTENCE = {
  always: 'are charged automatically unless they skip that order',
  never: 'customers check out each time',
  choice: 'customers choose whether they are charged automatically'
}

const orderingSentence = data => {
  if (!data.includes.repeat) return `${Customers(data)} order when they want and pay at checkout.`
  const start = defaultInterval(data)
  if (!start) return null
  const others = selectedIntervals(data)
    .filter(i => intervalKey(i) !== intervalKey(start))
    .map(i => lowerFirst(intervalLabel(i)))
  const choice = others.length ? `, or ${list(others)} if they choose` : ''
  const payment = PAYMENT_SENTENCE[data.ordering.autocharge] ?? PAYMENT_SENTENCE.always
  return `Orders from ${customersOf(data)} repeat ${lowerFirst(intervalLabel(start))}${choice}, and ${payment}.`
}

const grantPhrase = ({ grantInterval, grantDay, grantDate }) => {
  const day = upperFirst(grantDay ?? '')
  if (grantInterval === 'monthly') return `on the ${ordinal(grantDate)} of each month`
  if (grantInterval === 'every_two_weeks') return `every other ${day}`
  return `every ${day}`
}

const mealPlanSentence = (data, categories, currency) => {
  if (!data.includes.mealPlan || !stepValid('mealPlan', data)) return null
  const plan = data.mealPlan
  const category = categories.find(c => c.id === plan.categoryId)?.name
  const from = category ? ` from ${category}` : ''
  return `${Customers(data)} pay ${money(plan.priceCents, currency)} ${billingPhrase(plan.billingInterval)} for a meal plan of ${plan.itemCount} meals${from}, granted ${grantPhrase(plan)}.`
}

const packagesSentence = (data, packages) => {
  const names = [...pickedPackages(data, packages), ...newPackageNames(data)]
  return names.length ? `${Customers(data)} can also pick the ${list(names)} packages.` : null
}

const passSentence = (data, currency) => {
  const { pass } = data
  if (!pass) return null
  const name = pass.name.trim() || 'The pass'
  if (!pass.linked) return `${name} is no longer offered with ${tierLabel(data)}.`
  return `${Customers(data)} can add ${name} at checkout for ${money(pass.priceCents, currency)} ${billingPhrase(pass.billingInterval)}.`
}

const pricingSentence = (data, currency) => {
  const { pricing } = data
  const name = tierLabel(data)
  if (!data.includes.memberPrices) {
    return pricing.startedWithMemberPrices
      ? `Every product goes back to its regular price for ${customersOf(data)}.`
      : `${Customers(data)} pay regular prices.`
  }
  if (pricing.mode === 'keep') return `${Customers(data)} keep their current member prices.`
  if (pricing.mode === 'percent' && percentValid(pricing.percent)) {
    return `Every product's ${name} price is set to ${Number(pricing.percent)}% below its regular price.`
  }
  if (pricing.mode === 'amount' && amountValid(pricing.amountCents)) {
    return `Every product's ${name} price is set to ${money(pricing.amountCents, currency)} below its regular price, never below ${money(0, currency)}.`
  }
  return null
}

const recurringFeeSentence = (data, currency) => {
  const fee = data.fees.recurring
  const amount = `${Customers(data)} pay a ${money(fee.amountCents, currency)} ${recurringName(data)}`
  if (fee.intervalType === 'dollar_amount') {
    return `${amount} for every ${money(fee.every * CENTS_PER_DOLLAR, currency)} they spend.`
  }
  return `${amount} on ${feeCadence(fee, currency)}.`
}

const feeSentences = (data, currency) => {
  const { signUp, recurring } = data.fees
  const sentences = []
  if (signUp.enabled) {
    sentences.push(
      `${Customers(data)} pay a ${money(signUp.amountCents, currency)} ${signUpName(data)} on their first order.`
    )
  }
  if (recurring.enabled) sentences.push(recurringFeeSentence(data, currency))
  return sentences.length ? sentences : [`${tierSubject(data)} has no membership fees.`]
}

const settingMemberPrices = data =>
  data.includes.memberPrices && (data.pricing.mode === 'percent' || data.pricing.mode === 'amount')

const notes = data => {
  const name = tierLabel(data)
  const lines = []
  if (data.includes.repeat) lines.push(`Repeat orders start when you send a drop to the '${name}' members list.`)
  if (settingMemberPrices(data)) {
    lines.push(
      `Products you add later start at their regular price for ${customersOf(data)}. Set their ${name} price under a store's Product availability.`
    )
  }
  lines.push(`To give a single product a different ${name} price, change it under a store's Product availability.`)
  return lines
}

export const describeSetup = (
  data,
  {
    stores = [],
    currentDefault = null,
    currency = 'USD',
    currentIndex = 0,
    packages = [],
    categories = [],
    tiers = [],
    context = {},
    stepOk = null
  } = {}
) => ({
  rows: rows(data, { stores, currency, currentIndex, packages, context, stepOk }),
  sentences: [
    joiningSentence(data, stores, currentDefault),
    upgradesSentence(data, tiers),
    orderingSentence(data),
    mealPlanSentence(data, categories, currency),
    packagesSentence(data, packages),
    passSentence(data, currency),
    pricingSentence(data, currency),
    ...feeSentences(data, currency)
  ].filter(Boolean),
  notes: notes(data)
})

const exampleProduct = example => (example?.priceCents > 0 ? example : FALLBACK_EXAMPLE)

const memberPriceCents = (data, cents) => {
  const { mode, percent, amountCents } = data.pricing
  if (mode === 'percent' && percentValid(percent)) return discountedCents(cents, percent)
  if (mode === 'amount' && amountValid(amountCents)) return amountOffCents(cents, amountCents)
  return null
}

export const pricingExample = (data, { example = null, currency = 'USD' } = {}) => {
  const product = exampleProduct(example)
  const discounted = memberPriceCents(data, product.priceCents)
  if (discounted === null) return null
  const saved = product.priceCents - discounted
  if (saved <= 0) return null
  const full = money(product.priceCents, currency)
  return `A ${full} ${product.name} shows as ${money(discounted, currency)} with ${full} crossed out. At checkout customers read 'You saved ${money(saved, currency)} on this order' beside '${tierLabel(data)}'.`
}

export const recurringFeeExample = (data, currency = 'USD') => {
  const fee = data.fees.recurring
  if (!fee.enabled || !recurringFeeValid(fee)) return null
  const amount = money(fee.amountCents, currency)
  const name = tierLabel(data)
  if (fee.intervalType === 'dollar_amount') {
    return `Customers pay ${amount} each time their ${name} spending passes another ${money(fee.every * CENTS_PER_DOLLAR, currency)}.`
  }
  if (fee.every === 1) return `Customers pay ${amount} on every ${name} order.`
  const orders = [1, 2, 3].map(multiple => ordinal(fee.every * multiple))
  return `Customers pay ${amount} on their ${orders[0]}, ${orders[1]} and ${orders[2]} ${name} order.`
}

export const overrideNote = data => {
  const name = tierLabel(data)
  return `To give a single product a different ${name} price, open that product under a store's Product availability and change its ${name} price there.`
}

export const pricingTiersHint = (data, { example = null, currency = 'USD' } = {}) => {
  const name = tierLabel(data)
  const intro = `Pricing tiers are a store's order-size rewards, for example ${SAMPLE_TIER_PERCENT}% off at 8 items. For products marked 'Subject to pricing tiers', a reward open to ${name} applies on top of the ${name} price`
  const limited = `A reward limited to selected memberships applies only once you add ${name} to the reward.`
  const product = exampleProduct(example)
  const member = memberPriceCents(data, product.priceCents)
  if (member === null) return `${intro}. ${limited}`
  const stacked = discountedCents(member, SAMPLE_TIER_PERCENT)
  return `${intro}: a ${money(product.priceCents, currency)} ${product.name} at ${money(member, currency)} becomes ${money(stacked, currency)} with a further ${SAMPLE_TIER_PERCENT}% off. ${limited}`
}

export const dropHint = data =>
  `A drop opens ordering for a set of dates and messages the customers on the drop's list. A drop sent to the '${tierLabel(data)}' members list sets up each member's next repeat order.`

const packagesSeen = (data, packages) => {
  const names = [
    ...packages.filter(p => p.isOn !== false && (p.openToAll || data.packageIds.includes(p.id))).map(p => p.name),
    ...newPackageNames(data)
  ]
  return names.length
    ? `${Customers(data)} will see these packages: ${list(names)}.`
    : `${Customers(data)} will not see any packages at checkout.`
}

const turnedOffNote = (data, packages) => {
  const names = packages
    .filter(p => p.isOn === false && (p.openToAll || data.packageIds.includes(p.id)))
    .map(p => p.name)
  if (!names.length) return null
  return `${list(names)} ${names.length === 1 ? 'is' : 'are'} turned off, so customers do not see ${
    names.length === 1 ? 'that package' : 'them'
  } until you turn ${names.length === 1 ? 'that package' : 'them'} on in ${names.length === 1 ? 'its' : 'their'} store.`
}

export const packagesCopy = (data, packages) => {
  const customers = customersOf(data)
  const open = packages.filter(p => p.openToAll).map(p => p.name)
  const limited = packages.filter(p => !p.openToAll)
  const openNote =
    open.length === 1
      ? `You already have a package open to every membership, so ${customers} see ${open[0]} automatically.`
      : `You already have ${open.length} packages open to every membership, so ${customers} see ${list(open)} automatically.`
  return {
    limitedIntro: limited.length
      ? `These packages are limited to selected memberships. Tick the ones ${customers} can pick.`
      : null,
    openNote: open.length ? openNote : null,
    seen: packagesSeen(data, packages),
    hiddenNote: turnedOffNote(data, packages)
  }
}
